import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';

applyPlugin(jsPDF);

/**
 * Helper function to generate a CSV string from an array of objects.
 * It automatically handles headers and row data.
 *
 * @param {Array<Object>} formattedRows - The array of report data objects.
 * @returns {string} The CSV formatted data string.
 */
export const generateCsvData = (formattedRows) => {
  if (!formattedRows || formattedRows.length === 0) {
    return '';
  }

  const headers = Object.keys(formattedRows[0]);
  const headerString = headers.join(',');

  const rowStrings = formattedRows.map((row) => {
    return headers
      .map((header) => {
        const value = row[header];
        let displayValue = '';

        if (value !== null && typeof value === 'object') {
          displayValue = value.main;
        } else if (value !== null) {
          displayValue = value;
        }

        const escapedValue = String(displayValue).replace(/"/g, '""');
        return `"${escapedValue}"`;
      })
      .join(',');
  });

  return [headerString, ...rowStrings].join('\n');
};

/**
 * Helper function to download a CSV file.
 *
 * @param {string} csvData - The CSV formatted data string.
 * @param {string} fileName - The name of the file to download.
 */
export const downloadCsv = (csvData, fileName) => {
  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const insertLineBreaks = (text, maxLength) => {
  if (text.length <= maxLength) return text;

  const regex = new RegExp(`(.{1,${maxLength}})(\\s|$)`, 'g');
  return text.match(regex)?.join('\n') || text;
};

/**
 * Helper function to generate and download a PDF report using jsPDF.
 * This function requires the 'jspdf' and 'jspdf-autotable' libraries
 * to be included in your HTML file.
 *
 * @param {Array<Object>} formattedRows - The array of report data objects.
 * @param {string} title - The title of the report.
 * @param {string} fileName - The name of the file to download.
 */
export const generateAndDownloadPdf = (formattedRows, title, fileName) => {
  const headers = Object.keys(formattedRows[0] || {});

  const columns = headers.map((header) => ({ header, dataKey: header }));
  const rows = formattedRows.map((row) => {
    const rowData = {};
    headers.forEach((header) => {
      const value = row[header];
      if (value !== null && typeof value === 'object') {
        rowData[header] = value.main;
      } else if (value !== null) {
        rowData[header] = value;
      } else {
        rowData[header] = '-';
      }
    });
    return rowData;
  });

  const doc = new jsPDF('l', 'mm', 'a2');
  doc.text(title, 14, 20);

  const columnWidths = headers.map((header) => {
    const headerWidth = doc.getTextDimensions(header).w;
    const maxDataWidth = Math.max(
      ...rows.map((row) => {
        const text = row[header] || '';
        return doc.getTextDimensions(text).w;
      }),
    );
    return Math.min(Math.max(headerWidth, maxDataWidth), 60);
  });

  const columnStyles = {};
  columnWidths.forEach((width, index) => {
    columnStyles[index] = { cellWidth: width + 10 };
  });

  const margins = { top: 10, left: 5, right: 5, bottom: 5 };

  doc.autoTable({
    startY: 30,
    head: [columns.map((col) => col.header)],
    body: rows.map((row) =>
      columns.map((col) => {
        const text = row[col.dataKey] || '';

        return insertLineBreaks(text, 30);
      }),
    ),
    styles: {
      cellPadding: 2,
      overflow: 'linebreak',
      fontSize: 10,
      halign: 'left',
    },
    columnStyles: columnStyles,
    didDrawCell: (data) => {
      const cell = data.cell;
      if (cell.text.length > 0) {
        const textHeight = doc.getTextDimensions(cell.text).h;
        cell.height = Math.max(cell.height, textHeight + 4);
      }
    },
    margins,
  });

  doc.save(fileName);
};
